import { Box, Link, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Loading from '@/components/Loading'
import KeywordsAndTags, {
  addKeywords,
} from '@/components/DatabaseSearch/KeywordsAndTags'
import SearchBar, {
  type SearchBarError,
  type SearchBarState,
} from '@/components/DatabaseSearch/SearchBar'
import SearchFilterColumn from '@/components/DatabaseSearch/SearchFilterColumn'
import SearchResults, {
  COLUMNS,
} from '@/components/DatabaseSearch/SearchResults'
import SelectionsPanel from '@/components/DatabaseSearch/SelectionsPanel'
import {
  MUTED_TEXT,
  NAVY,
  visuallyHidden,
} from '@/components/DatabaseSearch/styles'
import { DatabaseSearchError } from '@/api/databaseSearchAPI'
import {
  DEFAULT_SEARCH_FILTERS,
  filtersSignature,
  hasAnyFilter,
  type DatabaseSearchFilters,
  type DatabaseSearchPage as SearchPage,
  type DatabaseSearchRequest,
  type DatabaseSearchResult,
  type SearchSortColumn,
} from '@/models/DatabaseSearch'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import * as Sentry from '@sentry/react'
import { downloadSelectionsCsv } from '@/utils/selectionsExport'
import { downloadSelectionsPdf } from '@/utils/selectionsPdf'
import {
  MAX_SELECTIONS,
  loadSearch,
  loadSelections,
  saveSearch,
  saveSelections,
  savedSearchKey,
  selectionsKey,
} from '@/utils/databaseSearchSession'
import { useDatabaseSearch, useSearchTags } from '@/utils/hooks/databaseSearch'
import { useIwi } from '@/utils/hooks/iwi'
import { useRegionSets, useRegions } from '@/utils/hooks/regions'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'

export const NO_FILTER_MESSAGE =
  'Choose a location, preference, tag, keyword, minimum amount or timing first.'
export const NO_IWI_MESSAGE =
  'Please select at least one iwi or uncheck the iwi funding option'

/** What a screen reader hears once a search, page or sort lands. */
export function resultsAnnouncement(
  page: SearchPage,
  request: DatabaseSearchRequest,
) {
  const match = page.total === 1 ? 'opportunity matches' : 'opportunities match'
  const pages = Math.max(1, Math.ceil(page.total / page.page_size))
  const column =
    COLUMNS.find((c) => c.key === request.sort_by)?.label ?? request.sort_by
  const direction = request.sort_dir === 'asc' ? 'ascending' : 'descending'
  return `${page.total} ${match}. Page ${page.page} of ${pages}, sorted by ${column}, ${direction}.`
}

export default function DatabaseSearchPage() {
  usePageTitle('Browse Database')
  const { user } = getCurrentUser()
  const { orgUuid } = usePausedOrg()
  if (!user?.uuid || !orgUuid) return <Loading />
  const storageKey = savedSearchKey(user.uuid, orgUuid)
  // Keyed so switching org remounts with that org's saved search.
  return (
    <SearchView
      key={storageKey}
      storageKey={storageKey}
      pickKey={selectionsKey(user.uuid, orgUuid)}
      orgUuid={orgUuid}
    />
  )
}

function SearchView({
  storageKey,
  pickKey,
  orgUuid,
}: {
  storageKey: string
  pickKey: string
  orgUuid: string
}) {
  const focusRef = useFocusManagement()
  const { token } = getCurrentUser()
  const { regions, isLoading: regionsLoading } = useRegions(token as string)
  const { regionSets, isLoading: regionSetsLoading } = useRegionSets(
    token as string,
  )
  const { iwi: iwiList, isLoading: iwiLoading } = useIwi(token as string)
  const { tags } = useSearchTags(orgUuid)
  const search = useDatabaseSearch(orgUuid)

  const [saved] = useState(() => loadSearch(storageKey))
  const [filters, setFilters] = useState<DatabaseSearchFilters>(saved.filters)
  const [locationsOpen, setLocationsOpen] = useState(saved.locationsOpen)
  const [keywordsOpen, setKeywordsOpen] = useState(saved.keywordsOpen)
  const [keywordDraft, setKeywordDraft] = useState(saved.keywordDraft)
  // The request behind the results on screen; sorting and paging reuse its filters.
  const [searched, setSearched] = useState<DatabaseSearchRequest | null>(
    saved.searched,
  )
  const [results, setResults] = useState<SearchPage | null>(saved.results)
  const [error, setError] = useState<SearchBarError | null>(null)
  const [iwiError, setIwiError] = useState<string | null>(null)

  useEffect(() => {
    saveSearch(storageKey, {
      filters,
      keywordDraft,
      locationsOpen,
      keywordsOpen,
      searched,
      results,
    })
  }, [
    storageKey,
    filters,
    keywordDraft,
    locationsOpen,
    keywordsOpen,
    searched,
    results,
  ])

  const [picked, setPicked] = useState<DatabaseSearchResult[]>(() =>
    loadSelections(pickKey),
  )
  const [selectionsOpen, setSelectionsOpen] = useState(false)
  const [pdfPreparing, setPdfPreparing] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const attempts = useRef(0)
  // Sorting then paging fast can land the older response last.
  const latestRequest = useRef(0)
  const iwiInputRef = useRef<HTMLInputElement>(null)
  // Focus after the render that fills in the error: the input reads it through
  // aria-describedby, and focusing first would read an empty description.
  const [iwiFocusRequest, setIwiFocusRequest] = useState(0)
  useEffect(() => {
    if (iwiFocusRequest) iwiInputRef.current?.focus()
  }, [iwiFocusRequest])
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  // Clear all removes View & export, the panel's usual focus return.
  const [focusSearchAfterPanel, setFocusSearchAfterPanel] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const pickedIds = new Set(picked.map((row) => row.opportunity_uuid))

  useEffect(() => {
    saveSelections(pickKey, picked)
  }, [pickKey, picked])

  async function downloadPdf() {
    setDownloadError(null)
    setPdfPreparing(true)
    try {
      await downloadSelectionsPdf(picked)
      setSelectionsOpen(false)
    } catch (e) {
      Sentry.captureException(e)
      setDownloadError("Couldn't prepare the PDF. Please try again.")
    } finally {
      setPdfPreparing(false)
    }
  }

  function togglePick(row: DatabaseSearchResult) {
    setPicked((prev) => {
      if (prev.some((p) => p.opportunity_uuid === row.opportunity_uuid)) {
        return prev.filter((p) => p.opportunity_uuid !== row.opportunity_uuid)
      }
      return prev.length >= MAX_SELECTIONS ? prev : [...prev, row]
    })
  }

  const updateFilters = (patch: Partial<DatabaseSearchFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setIwiError(null)
    setError(null)
  }

  function showError(next: Omit<SearchBarError, 'attempt'>) {
    attempts.current += 1
    setError({ ...next, attempt: attempts.current })
  }

  async function run(request: DatabaseSearchRequest) {
    const id = ++latestRequest.current
    setError(null)
    setAnnouncement('Searching…')
    try {
      const page = await search.mutateAsync(request)
      if (id !== latestRequest.current) return
      setResults(page)
      setSearched(request)
      setAnnouncement(resultsAnnouncement(page, request))
    } catch (e) {
      if (id !== latestRequest.current) return
      // The search bar's own status line reads the error.
      setAnnouncement('')
      showError(
        e instanceof DatabaseSearchError
          ? { message: e.message, rateLimited: e.status === 429 }
          : { message: 'Search failed. Please try again.' },
      )
    }
  }

  function onSearch() {
    // Text still in the keyword box counts as a keyword.
    let nextFilters = keywordDraft.trim()
      ? { ...filters, keywords: addKeywords(filters.keywords, keywordDraft) }
      : filters
    // A saved search can hold a tag that has since left the list; the server
    // would refuse it, and the page has no chip left to untick it with.
    if (tags) {
      const known = (list: string[]) => list.filter((tag) => tags.includes(tag))
      const include = known(nextFilters.include_tags)
      const exclude = known(nextFilters.exclude_tags)
      if (
        include.length !== nextFilters.include_tags.length ||
        exclude.length !== nextFilters.exclude_tags.length
      ) {
        nextFilters = {
          ...nextFilters,
          include_tags: include,
          exclude_tags: exclude,
        }
      }
    }
    if (nextFilters !== filters) {
      setFilters(nextFilters)
      setKeywordDraft('')
    }
    if (nextFilters.iwi_eligible && nextFilters.iwi_ids.length === 0) {
      setIwiError(NO_IWI_MESSAGE)
      setIwiFocusRequest((n) => n + 1)
      return
    }
    if (!hasAnyFilter(nextFilters)) {
      showError({ message: NO_FILTER_MESSAGE })
      return
    }
    run({
      filters: nextFilters,
      page: 1,
      sort_by: searched?.sort_by ?? 'funder',
      sort_dir: searched?.sort_dir ?? 'asc',
    })
  }

  function onSort(column: SearchSortColumn) {
    if (!searched) return
    const sortDir =
      searched.sort_by === column && searched.sort_dir === 'asc'
        ? 'desc'
        : 'asc'
    run({ ...searched, page: 1, sort_by: column, sort_dir: sortDir })
  }

  function onPage(page: number) {
    if (searched) run({ ...searched, page })
  }

  function onReset() {
    latestRequest.current += 1
    setFilters(DEFAULT_SEARCH_FILTERS)
    setKeywordDraft('')
    setLocationsOpen(false)
    setKeywordsOpen(false)
    setSearched(null)
    setResults(null)
    setError(null)
    setIwiError(null)
  }

  const stale =
    searched !== null &&
    filtersSignature(filters) !== filtersSignature(searched.filters)
  const barState: SearchBarState = !searched
    ? 'ready'
    : stale
      ? 'stale'
      : 'searched'

  return (
    <Box sx={{ px: { xs: 2, md: 15 }, mt: 2, pb: { xs: 36, sm: 30, md: 14 } }}>
      <Typography
        variant="h1"
        component="h1"
        ref={focusRef}
        tabIndex={-1}
        sx={{
          mb: 1.5,
          textAlign: 'left',
          fontSize: '34px',
          '&:focus': { outline: 'none' },
        }}
      >
        Browse Database
      </Typography>
      {/* The heading renders while regions load, so it is there to take focus. */}
      {regionsLoading || regionSetsLoading ? (
        <Loading />
      ) : (
        <>
          <Typography sx={{ mb: 4, maxWidth: 760, color: MUTED_TEXT }}>
            Here you can explore the full funding database using filters. To
            have Fundsorter automatically match you with the best fit
            opportunities for your organisation and draft your applications, go
            to{' '}
            <Link
              component={RouterLink}
              to="/funding-requests"
              sx={{ color: NAVY }}
            >
              Find Funding
            </Link>{' '}
            and create a request.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(320px, 420px) minmax(0, 1fr)',
              },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <SearchFilterColumn
              filters={filters}
              onChange={updateFilters}
              regions={regions ?? []}
              regionSets={regionSets ?? []}
              iwiList={iwiList}
              iwiLoading={iwiLoading}
              iwiError={iwiError}
              iwiInputRef={iwiInputRef}
              locationsOpen={locationsOpen}
              onLocationsOpenChange={setLocationsOpen}
              keywordsAndTags={
                <KeywordsAndTags
                  filters={filters}
                  onChange={updateFilters}
                  tags={tags ?? []}
                  open={keywordsOpen}
                  onOpenChange={setKeywordsOpen}
                  keywordDraft={keywordDraft}
                  onKeywordDraftChange={setKeywordDraft}
                />
              }
            />
            {/* Stretched to the filter column's height: the search bar sticks
                inside this column, so a short column gives it nowhere to stick. */}
            <Box sx={{ minWidth: 0, alignSelf: 'stretch' }}>
              <SearchBar
                state={barState}
                error={error}
                searching={search.isPending}
                onReset={onReset}
                onSearch={onSearch}
                pickedCount={picked.length}
                onViewSelections={() => setSelectionsOpen(true)}
                searchButtonRef={searchButtonRef}
              />
              {results && searched && (
                <SearchResults
                  page={results}
                  sortBy={searched.sort_by}
                  sortDir={searched.sort_dir}
                  stale={stale}
                  onSort={onSort}
                  onPage={onPage}
                  pickedIds={pickedIds}
                  onTogglePick={togglePick}
                />
              )}
            </Box>
          </Box>
          <Box role="status" sx={visuallyHidden}>
            {announcement}
          </Box>
          <SelectionsPanel
            open={selectionsOpen}
            onClose={() => {
              setSelectionsOpen(false)
              setDownloadError(null)
            }}
            picked={picked}
            onRemove={togglePick}
            onClearAll={() => {
              setPicked([])
              setFocusSearchAfterPanel(true)
              setSelectionsOpen(false)
            }}
            restoreFocus={!focusSearchAfterPanel}
            onExited={() => {
              if (focusSearchAfterPanel) searchButtonRef.current?.focus()
              setFocusSearchAfterPanel(false)
            }}
            // Selections stay after a download, so the user can add more and download again.
            onDownloadCsv={() => {
              downloadSelectionsCsv(picked)
              setSelectionsOpen(false)
            }}
            onDownloadPdf={downloadPdf}
            pdfPreparing={pdfPreparing}
            downloadError={downloadError}
          />
        </>
      )}
    </Box>
  )
}
